import * as React from 'react';

import { Select as SelectRadix } from 'radix-ui';
import { CheckIcon } from 'lucide-react';
import { Select as UISelect, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/shared/lib/utils';
import { _t } from '@/i18n/t';

interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface SelectProps {
  value?: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  label?: React.ReactNode;
  labelClassName?: string;
  wrapperClassName?: string;
  contentClassName?: string;
  triggerClassName?: string;
  disabled?: boolean;
  clearable?: boolean;
  clearLabel?: string;
  emptyText?: string;
  'data-testid'?: string;
}

const CLEAR_VALUE = '__clear__';

const Select = ({
  value,
  onValueChange,
  options,
  placeholder,
  label,
  labelClassName,
  wrapperClassName,
  contentClassName,
  triggerClassName,
  disabled,
  clearable,
  clearLabel = _t('select.clearSelection'),
  emptyText,
  'data-testid': testId,
}: SelectProps) => {
  const handleValueChange = (selected: string) => {
    onValueChange(selected === CLEAR_VALUE ? '' : selected);
  };

  const selectedLabel = value ? options.find((o) => o.value === value)?.label : undefined;
  const hasOptions = options.length > 0;

  const content = (
    <UISelect
      value={value}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectTrigger
        data-testid={testId}
        data-th-typography="form-control"
        title={selectedLabel}
        className={cn("!h-11 !w-full !rounded-xl !border !border-solid !border-input-border !bg-white px-4 !text-control font-normal leading-normal text-input-text shadow-none outline-none overflow-hidden data-[placeholder]:text-input-text/80 disabled:cursor-not-allowed disabled:!bg-[rgba(229,231,235,0.35)] disabled:!border-[rgba(229,231,235,0.6)] disabled:!text-muted-foreground disabled:[&_svg]:opacity-40 [&_svg]:text-[#818a99] [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:truncate [&_[data-slot=select-value]]:text-control [&_[data-slot=select-value]]:font-normal [&_[data-slot=select-value]]:leading-normal", triggerClassName)}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent
        position="popper"
        data-th-typography="form-control"
        className={cn('mt-2 z-50 max-h-[250px] w-[var(--radix-select-trigger-width)] !rounded-xl !border !border-solid !border-input-border !bg-white p-1 text-control font-normal text-input-text shadow-[0_0_0_1px_rgba(213,221,231,0.65),0_8px_20px_rgba(15,23,42,0.08)]', contentClassName)}
      >
        {!hasOptions ? (
          <div
            data-testid={testId ? `${testId}-empty` : undefined}
            className="px-3 py-2.5 !text-control font-normal text-muted-foreground"
          >
            {emptyText ?? _t('select.noOptions')}
          </div>
        ) : null}
        {clearable && value && hasOptions ? (
          <SelectItem
            value={CLEAR_VALUE}
            className="rounded-md py-2 cursor-pointer pr-3 !text-control font-normal text-input-text/50 outline-none focus:bg-[#eef2f6]"
          >
            {clearLabel}
          </SelectItem>
        ) : null}
        {options.map((option) =>
          option.description ? (
            <SelectRadix.Item
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className="relative flex w-full cursor-pointer items-start rounded-md py-2 pr-8 pl-1.5 outline-none select-none focus:bg-[#eef2f6] data-disabled:pointer-events-none data-disabled:opacity-50"
            >
              <span className="pointer-events-none absolute right-2 top-[10px] flex size-4 items-center justify-center">
                <SelectRadix.ItemIndicator>
                  <CheckIcon className="size-4 text-input-text" />
                </SelectRadix.ItemIndicator>
              </span>
              <div className="flex flex-col gap-1">
                <SelectRadix.ItemText>
                  <span className="text-control font-normal text-input-text">{option.label}</span>
                </SelectRadix.ItemText>
                <span data-th-typography="form-option-description" className="text-form-option-description font-normal text-input-text/60">
                  {option.description}
                </span>
              </div>
            </SelectRadix.Item>
          ) : (
            <SelectItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className="rounded-md py-2 cursor-pointer pr-3 !text-control font-normal text-input-text outline-none focus:bg-[#eef2f6]"
            >
              {option.label}
            </SelectItem>
          )
        )}
      </SelectContent>
    </UISelect>
  );

  if (!label) return content;

  return (
    <div className={cn('space-y-1.5', wrapperClassName)}>
      <label data-th-typography="form-label" className={cn('block text-form-label text-label', labelClassName)}>
        {label}
      </label>
      {content}
    </div>
  );
};

export default Select;
